import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import cn from 'classnames';

import { TRANSPORT } from 'helpers/routes';
import { useSearchLookups } from 'hooks/useSearchLookups';
import { SearchIcon } from 'components/common/Icons';
import styles from './styles.module.scss';

interface TransportQuickSearchBarProps {
  isExpanded: boolean;
  onToggleExpanded: () => void;
  authenticated: boolean;
  hideExpandButton?: boolean;
}

interface AddressValue {
  country_code?: string;
  post_code?: string;
  city?: string;
  country?: string;
  full_address?: string;
}

interface AddressField {
  value?: AddressValue;
  label?: string;
}

interface FormValues {
  available_from?: string;
  available_to?: string;
  transport_bodytype_ids?: number | string;
  transports_loading_types?: (number | string)[];
  payment_types_ids?: (number | string)[];
  loading_address?: AddressField | null;
  unloading_address?: AddressField | null;
}

const TransportQuickSearchBar: React.FC<TransportQuickSearchBarProps> = ({
  isExpanded,
  onToggleExpanded,
  authenticated,
  hideExpandButton = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { values } = useFormikContext<FormValues>();
  const { getBodyTypeLabel, getLoadingTypeLabels, getPaymentTypeLabels } = useSearchLookups();

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatLocation = (address?: AddressField | null) => {
    if (!address?.value) return null;
    const { country_code, post_code, city, country } = address.value;
    const parts: string[] = [];
    if (country_code) parts.push(country_code.toUpperCase());
    if (post_code) parts.push(post_code);
    const locationName = city || country;
    if (locationName) parts.push(locationName);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const fromLocation = formatLocation(values.loading_address);
  const toLocation = formatLocation(values.unloading_address);
  const routeText = fromLocation || toLocation
    ? `${fromLocation || '—'} - ${toLocation || '—'}`
    : null;

  const hasDateFilter = values.available_from || values.available_to;
  const dateChipText = hasDateFilter
    ? `${t('transportSearch.dateFilter')}: ${formatDate(values.available_from)} - ${formatDate(values.available_to)}`
    : null;

  const bodyTypeLabel = getBodyTypeLabel(values.transport_bodytype_ids);
  const loadingTypeLabels = getLoadingTypeLabels(values.transports_loading_types);
  const paymentTypeLabels = getPaymentTypeLabels(values.payment_types_ids);

  const handleAddTransport = () => {
    navigate(TRANSPORT.addToExchange);
  };

  // Hide chips when status filter is active (hideExpandButton is true in that case)
  const showChips = !hideExpandButton;

  return (
    <div className={cn(styles.quickSearchBar, { [styles.withTabs]: authenticated })}>
      <div className={styles.leftSection}>
        <span className={styles.label}>{t('transportSearch.currentParams')}:</span>
        {showChips && routeText && (
          <div className={styles.dateChip}>
            <span>{t('transportSearch.route')}: {routeText}</span>
          </div>
        )}
        {showChips && dateChipText && (
          <div className={styles.dateChip}>
            <span>{dateChipText}</span>
          </div>
        )}
        {showChips && bodyTypeLabel && (
          <div className={styles.dateChip}>
            <span>{t('transportSearch.body')}: {bodyTypeLabel}</span>
          </div>
        )}
        {showChips && loadingTypeLabels && (
          <div className={styles.dateChip}>
            <span>{t('transportSearch.loadingType')}: {loadingTypeLabels}</span>
          </div>
        )}
        {showChips && paymentTypeLabels && (
          <div className={styles.dateChip}>
            <span>{t('transportSearch.rateType')}: {paymentTypeLabels}</span>
          </div>
        )}
      </div>
      <div className={styles.rightSection}>
        {!hideExpandButton && (
          <button
            type="button"
            className={styles.expandedSearchBtn}
            onClick={onToggleExpanded}
          >
            <SearchIcon />
            {t('transportSearch.expandedSearch')}
          </button>
        )}
        {authenticated && (
          <button
            type="button"
            className={styles.addTransportBtn}
            onClick={handleAddTransport}
          >
            {t('transportSearch.addTransport')}
          </button>
        )}
      </div>
    </div>
  );
};

export default TransportQuickSearchBar;
